import React, { useMemo, useState } from 'react';
import {
  formatRub,
  MONTH_OPTIONS,
  normalizePositionFormData,
  QUARTERLY_SALARY_REVIEW_TIMING_OPTIONS,
  SALARY_REVIEW_FREQUENCY_OPTIONS,
  payoutTimingKind,
  salaryReviewTimingKind,
  STI_FORMULA_TYPE_OPTIONS,
  STI_SIZING_BASIS_OPTIONS,
  LTIP_DURATION_OPTIONS,
  LTIP_DURATION_OTHER,
  LTIP_PROGRAM_TYPE_OPTIONS,
  LTIP_VALUE_MODE_OPTIONS,
  LTIP_VESTING_OPTIONS,
  syncAnnualBaseFromMonthly,
  validateBaseSalaryStep,
  validateStiStep,
  validateLtipStep,
  parsePercentInput,
  type PositionFormData,
  type StiFormulaType,
  type StiSizingBasis,
  type LtipProgramType,
  type LtipValueMode,
} from './positionFormModel';
import { MpField, MpStepper, SensitiveDataNotice } from './MarketProfileShared';

const STEPS = ['Базовый оклад', 'STI', 'LTIP'];

type Props = {
  initial: PositionFormData;
  onComplete: (data: PositionFormData) => void;
  onBack: () => void;
};

function sanitizePercentInput(value: string): string {
  let normalized = value.replace(/[^\d.,]/g, '').replace(',', '.');
  const dotIndex = normalized.indexOf('.');
  if (dotIndex === -1) return normalized;
  const intPart = normalized.slice(0, dotIndex);
  const fracPart = normalized.slice(dotIndex + 1).replace(/\./g, '');
  return `${intPart}.${fracPart}`;
}

function RadioOptionList({
  name,
  value,
  options,
  onChange,
}: {
  name: string;
  value: string;
  options: readonly string[];
  onChange: (next: string) => void;
}) {
  return (
    <div className="dcf-mp-option-list">
      {options.map((option) => (
        <label
          key={option}
          className={`dcf-mp-answer ${value === option ? 'dcf-mp-answer--selected' : ''}`}
        >
          <input
            type="radio"
            name={name}
            checked={value === option}
            onChange={() => onChange(option)}
          />
          <span>{option}</span>
        </label>
      ))}
    </div>
  );
}

function RadioValueList<T extends string>({
  name,
  value,
  options,
  onChange,
}: {
  name: string;
  value: T | '';
  options: { value: T; label: string }[];
  onChange: (next: T) => void;
}) {
  return (
    <div className="dcf-mp-option-list">
      {options.map((option) => (
        <label
          key={option.value}
          className={`dcf-mp-answer ${value === option.value ? 'dcf-mp-answer--selected' : ''}`}
        >
          <input
            type="radio"
            name={name}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <span>{option.label}</span>
        </label>
      ))}
    </div>
  );
}

function BaseSalaryStep({
  form,
  setForm,
}: {
  form: PositionFormData;
  setForm: React.Dispatch<React.SetStateAction<PositionFormData>>;
}) {
  const set = <K extends keyof PositionFormData>(key: K, value: PositionFormData[K]) => {
    setForm((prev) => syncAnnualBaseFromMonthly({ ...prev, [key]: value }));
  };

  const setFrequency = (frequency: string) => {
    setForm((prev) => syncAnnualBaseFromMonthly({
      ...prev,
      salaryReviewFrequency: frequency,
      salaryReviewDate: '',
      salaryReviewDate2: '',
    }));
  };

  const timing = salaryReviewTimingKind(form.salaryReviewFrequency);

  return (
    <>
      <h2 className="dcf-mp-form-section-title">Базовый оклад</h2>
      <SensitiveDataNotice compact />
      <div className="dcf-mp-form-grid dcf-mp-form-grid--stacked">
        <MpField
          label="Каков размер вашей фиксированной оплаты труда в рублях в месяц? (оклад и другие выплаты, не зависящие от результативности работы) *GROSS"
          required
          htmlFor="mp-base-monthly"
        >
          <input
            id="mp-base-monthly"
            className="dcf-input"
            inputMode="decimal"
            placeholder="Например: 500 000"
            value={form.baseGrossSalaryMonthly}
            onChange={(e) => set('baseGrossSalaryMonthly', e.target.value)}
          />
        </MpField>

        <MpField
          label="Сколько раз в год компания обычно пересматривает вашу заработную плату (базовый оклад)?"
          required
        >
          <RadioOptionList
            name="mp-salary-freq"
            value={form.salaryReviewFrequency}
            options={SALARY_REVIEW_FREQUENCY_OPTIONS}
            onChange={setFrequency}
          />
        </MpField>

        {timing === 'single' && (
          <MpField label="Когда производится пересмотр?" required>
            <RadioOptionList
              name="mp-salary-month"
              value={form.salaryReviewDate}
              options={MONTH_OPTIONS}
              onChange={(month) => set('salaryReviewDate', month)}
            />
          </MpField>
        )}

        {timing === 'double' && (
          <>
            <MpField label="Когда производится пересмотр? (первый месяц)" required>
              <RadioOptionList
                name="mp-salary-month-1"
                value={form.salaryReviewDate}
                options={MONTH_OPTIONS}
                onChange={(month) => {
                  setForm((prev) => syncAnnualBaseFromMonthly({
                    ...prev,
                    salaryReviewDate: month,
                    salaryReviewDate2: prev.salaryReviewDate2 === month ? '' : prev.salaryReviewDate2,
                  }));
                }}
              />
            </MpField>
            <MpField label="Когда производится пересмотр? (второй месяц)" required>
              <RadioOptionList
                name="mp-salary-month-2"
                value={form.salaryReviewDate2}
                options={MONTH_OPTIONS.filter((m) => m !== form.salaryReviewDate)}
                onChange={(month) => set('salaryReviewDate2', month)}
              />
            </MpField>
          </>
        )}

        {timing === 'quarterly' && (
          <MpField label="Когда производится пересмотр?" required>
            <RadioOptionList
              name="mp-salary-quarterly"
              value={form.salaryReviewDate}
              options={QUARTERLY_SALARY_REVIEW_TIMING_OPTIONS}
              onChange={(option) => set('salaryReviewDate', option)}
            />
          </MpField>
        )}

        <MpField
          label="На основании каких критериев производится пересмотр индивидуального оклада?"
          htmlFor="mp-salary-criteria"
          hint="Опишите своими словами — например, результат, инфляция, рыночный бенчмарк."
        >
          <textarea
            id="mp-salary-criteria"
            className="dcf-textarea"
            rows={4}
            value={form.salaryReviewCriteria}
            onChange={(e) => set('salaryReviewCriteria', e.target.value)}
          />
        </MpField>
      </div>
    </>
  );
}

function StiStep({
  form,
  setForm,
}: {
  form: PositionFormData;
  setForm: React.Dispatch<React.SetStateAction<PositionFormData>>;
}) {
  const patch = (patchData: Partial<PositionFormData>) => {
    setForm((prev) => syncAnnualBaseFromMonthly({ ...prev, ...patchData }));
  };

  const setPayoutFrequency = (frequency: string) => {
    patch({
      stiPayoutFrequency: frequency,
      stiPayoutMonth: '',
      stiPayoutMonth2: '',
    });
  };

  const setSizingBasis = (basis: StiSizingBasis) => {
    patch({
      stiSizingBasis: basis,
      stiCorporateKpiExamples: '',
      stiIndividualKpiExamples: '',
      stiCommissionEvent: '',
      stiCommissionPct: '',
      stiFormulaType: '',
      stiCorporateKpiWeight: '',
      stiIndividualKpiWeight: '',
    });
  };

  const setFormulaType = (formulaType: StiFormulaType) => {
    patch({
      stiFormulaType: formulaType,
      stiCorporateKpiWeight: formulaType === 'additive' ? form.stiCorporateKpiWeight : '',
      stiIndividualKpiWeight: formulaType === 'additive' ? form.stiIndividualKpiWeight : '',
    });
  };

  const timing = payoutTimingKind(form.stiPayoutFrequency);
  const targetPct = parsePercentInput(form.stiTargetPct);
  const hasSti = targetPct != null && targetPct > 0;

  return (
    <>
      <h2 className="dcf-mp-form-section-title">STI</h2>
      <SensitiveDataNotice compact />
      <div className="dcf-mp-form-grid dcf-mp-form-grid--stacked">
        <MpField
          label="Каков целевой размер вашей премии (STI), зависящей от результатов работы, в % от годового базового оклада?"
          required
          hint="0 — если переменной премии нет."
          htmlFor="mp-sti-target-pct"
        >
          <input
            id="mp-sti-target-pct"
            className="dcf-input"
            inputMode="decimal"
            placeholder="Например: 30"
            value={form.stiTargetPct}
            onChange={(e) => patch({ stiTargetPct: sanitizePercentInput(e.target.value) })}
          />
        </MpField>

        {hasSti && (
          <>
            <MpField label="Как часто выплачивается эта премия?" required>
              <RadioOptionList
                name="mp-sti-payout-freq"
                value={form.stiPayoutFrequency}
                options={SALARY_REVIEW_FREQUENCY_OPTIONS}
                onChange={setPayoutFrequency}
              />
            </MpField>

            {timing === 'single' && (
              <MpField label="Когда выплачивается премия?" required>
                <RadioOptionList
                  name="mp-sti-payout-month"
                  value={form.stiPayoutMonth}
                  options={MONTH_OPTIONS}
                  onChange={(month) => patch({ stiPayoutMonth: month })}
                />
              </MpField>
            )}

            {timing === 'double' && (
              <>
                <MpField label="Когда выплачивается премия? (первый месяц)" required>
                  <RadioOptionList
                    name="mp-sti-payout-month-1"
                    value={form.stiPayoutMonth}
                    options={MONTH_OPTIONS}
                    onChange={(month) => patch({
                      stiPayoutMonth: month,
                      stiPayoutMonth2: form.stiPayoutMonth2 === month ? '' : form.stiPayoutMonth2,
                    })}
                  />
                </MpField>
                <MpField label="Когда выплачивается премия? (второй месяц)" required>
                  <RadioOptionList
                    name="mp-sti-payout-month-2"
                    value={form.stiPayoutMonth2}
                    options={MONTH_OPTIONS.filter((m) => m !== form.stiPayoutMonth)}
                    onChange={(month) => patch({ stiPayoutMonth2: month })}
                  />
                </MpField>
              </>
            )}

            {timing === 'quarterly' && (
              <MpField label="Когда выплачивается премия?" required>
                <RadioOptionList
                  name="mp-sti-payout-quarterly"
                  value={form.stiPayoutMonth}
                  options={QUARTERLY_SALARY_REVIEW_TIMING_OPTIONS}
                  onChange={(option) => patch({ stiPayoutMonth: option })}
                />
              </MpField>
            )}

            <MpField label="От чего в большей степени зависит размер выплачиваемой вам премии?" required>
              <RadioValueList
                name="mp-sti-sizing-basis"
                value={form.stiSizingBasis}
                options={STI_SIZING_BASIS_OPTIONS}
                onChange={setSizingBasis}
              />
            </MpField>

            {form.stiSizingBasis === 'kpi' && (
              <>
                <MpField
                  label="Какие показатели эффективности всей организации влияют на вашу премию?"
                  required
                  htmlFor="mp-sti-corp-kpi"
                >
                  <textarea
                    id="mp-sti-corp-kpi"
                    className="dcf-textarea"
                    rows={3}
                    placeholder="2–3 показателя через запятую"
                    value={form.stiCorporateKpiExamples}
                    onChange={(e) => patch({ stiCorporateKpiExamples: e.target.value })}
                  />
                </MpField>
                <MpField
                  label="Какие показатели эффективности непосредственно вашей должности / роли влияют на вашу премию?"
                  required
                  htmlFor="mp-sti-ind-kpi"
                >
                  <textarea
                    id="mp-sti-ind-kpi"
                    className="dcf-textarea"
                    rows={3}
                    placeholder="2–3 показателя через запятую"
                    value={form.stiIndividualKpiExamples}
                    onChange={(e) => patch({ stiIndividualKpiExamples: e.target.value })}
                  />
                </MpField>
                <MpField
                  label='Формула: сумма премии зависит от веса показателей или используется мультипликатор (корпоративные × индивидуальные)?'
                  required
                >
                  <RadioValueList
                    name="mp-sti-formula-type"
                    value={form.stiFormulaType}
                    options={STI_FORMULA_TYPE_OPTIONS}
                    onChange={setFormulaType}
                  />
                </MpField>
                {form.stiFormulaType === 'additive' && (
                  <>
                    <MpField
                      label="Каковы веса корпоративных показателей в определении размера вашей премии?"
                      required
                      hint="% веса показателей"
                      htmlFor="mp-sti-corp-weight"
                    >
                      <input
                        id="mp-sti-corp-weight"
                        className="dcf-input"
                        inputMode="decimal"
                        placeholder="Например: 40"
                        value={form.stiCorporateKpiWeight}
                        onChange={(e) => patch({ stiCorporateKpiWeight: sanitizePercentInput(e.target.value) })}
                      />
                    </MpField>
                    <MpField
                      label="Каковы веса индивидуальных показателей в определении размера вашей премии?"
                      required
                      hint="% веса показателей"
                      htmlFor="mp-sti-ind-weight"
                    >
                      <input
                        id="mp-sti-ind-weight"
                        className="dcf-input"
                        inputMode="decimal"
                        placeholder="Например: 60"
                        value={form.stiIndividualKpiWeight}
                        onChange={(e) => patch({ stiIndividualKpiWeight: sanitizePercentInput(e.target.value) })}
                      />
                    </MpField>
                  </>
                )}
              </>
            )}

            {form.stiSizingBasis === 'commission' && (
              <>
                <MpField
                  label="К какому ключевому событию привязана комиссия?"
                  required
                  htmlFor="mp-sti-commission-event"
                >
                  <input
                    id="mp-sti-commission-event"
                    className="dcf-input"
                    placeholder="Например: закрытие сделки, привлечение инвестора"
                    value={form.stiCommissionEvent}
                    onChange={(e) => patch({ stiCommissionEvent: e.target.value })}
                  />
                </MpField>
                <MpField
                  label="Какой процент комиссии от события вы получаете?"
                  required
                  hint="Укажите фиксированный % или диапазон, например: 5 или 3–7"
                  htmlFor="mp-sti-commission-pct"
                >
                  <input
                    id="mp-sti-commission-pct"
                    className="dcf-input"
                    placeholder="Например: 5 или 3–7"
                    value={form.stiCommissionPct}
                    onChange={(e) => patch({ stiCommissionPct: e.target.value })}
                  />
                </MpField>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}

function LtipValueInput({
  label,
  required,
  mode,
  pct,
  amount,
  onModeChange,
  onPctChange,
  onAmountChange,
  pctId,
  amountId,
}: {
  label: string;
  required?: boolean;
  mode: LtipValueMode;
  pct: string;
  amount: string;
  onModeChange: (next: LtipValueMode) => void;
  onPctChange: (next: string) => void;
  onAmountChange: (next: string) => void;
  pctId: string;
  amountId: string;
}) {
  return (
    <MpField label={label} required={required} hint="% от годового базового оклада / абсолютное значение, ₽ gross">
      <RadioValueList
        name={`${pctId}-mode`}
        value={mode}
        options={LTIP_VALUE_MODE_OPTIONS.filter((o) => o.value !== '')}
        onChange={onModeChange}
      />
      {mode === 'percent' && (
        <input
          id={pctId}
          className="dcf-input mt-2"
          inputMode="decimal"
          placeholder="Например: 150"
          value={pct}
          onChange={(e) => onPctChange(sanitizePercentInput(e.target.value))}
        />
      )}
      {mode === 'absolute' && (
        <input
          id={amountId}
          className="dcf-input mt-2"
          inputMode="decimal"
          placeholder="Например: 5 000 000"
          value={amount}
          onChange={(e) => onAmountChange(e.target.value)}
        />
      )}
    </MpField>
  );
}

function LtipStep({
  form,
  setForm,
}: {
  form: PositionFormData;
  setForm: React.Dispatch<React.SetStateAction<PositionFormData>>;
}) {
  const patch = (patchData: Partial<PositionFormData>) => {
    setForm((prev) => syncAnnualBaseFromMonthly({ ...prev, ...patchData }));
  };

  const setProgramType = (programType: LtipProgramType) => {
    if (programType === 'none') {
      patch({
        ltipProgramType: programType,
        ltipFormOther: '',
        ltipProgramValueMode: '',
        ltipProgramValuePct: '',
        ltipProgramValueAmount: '',
        ltipProgramDuration: '',
        ltipProgramDurationOther: '',
        ltipVestingType: '',
        ltipLastPayoutMode: '',
        ltipLastPayoutPct: '',
        ltipLastPayoutAmount: '',
        ltipForm: '',
        ltipAmount: '',
        ltipVestingSchedule: '',
      });
      return;
    }
    patch({ ltipProgramType: programType });
  };

  const hasLtip = form.ltipProgramType !== '' && form.ltipProgramType !== 'none';

  return (
    <>
      <h2 className="dcf-mp-form-section-title">LTIP</h2>
      <SensitiveDataNotice compact />
      <div className="dcf-mp-form-grid dcf-mp-form-grid--stacked">
        <MpField
          label="Являетесь ли вы участником LTIP? Если да, к какому типу относится программа?"
          required
        >
          <RadioValueList
            name="mp-ltip-program-type"
            value={form.ltipProgramType}
            options={LTIP_PROGRAM_TYPE_OPTIONS}
            onChange={setProgramType}
          />
        </MpField>

        {form.ltipProgramType === 'other' && (
          <MpField label="Опишите тип программы LTIP" required htmlFor="mp-ltip-form-other">
            <input
              id="mp-ltip-form-other"
              className="dcf-input"
              placeholder="Например: profit share, co-invest"
              value={form.ltipFormOther}
              onChange={(e) => patch({ ltipFormOther: e.target.value })}
            />
          </MpField>
        )}

        {hasLtip && (
          <>
            <LtipValueInput
              label="Оцените совокупную стоимость вашего долгосрочного бонуса (всей программы LTI)"
              required
              mode={form.ltipProgramValueMode}
              pct={form.ltipProgramValuePct}
              amount={form.ltipProgramValueAmount}
              pctId="mp-ltip-program-value-pct"
              amountId="mp-ltip-program-value-amount"
              onModeChange={(mode) => patch({
                ltipProgramValueMode: mode,
                ltipProgramValuePct: mode === 'percent' ? form.ltipProgramValuePct : '',
                ltipProgramValueAmount: mode === 'absolute' ? form.ltipProgramValueAmount : '',
              })}
              onPctChange={(value) => patch({ ltipProgramValuePct: value })}
              onAmountChange={(value) => patch({ ltipProgramValueAmount: value })}
            />

            <MpField label="Какова общая длительность программы LTI?" required>
              <RadioOptionList
                name="mp-ltip-duration"
                value={form.ltipProgramDuration}
                options={LTIP_DURATION_OPTIONS}
                onChange={(duration) => patch({
                  ltipProgramDuration: duration,
                  ltipProgramDurationOther: duration === LTIP_DURATION_OTHER ? form.ltipProgramDurationOther : '',
                })}
              />
            </MpField>

            {form.ltipProgramDuration === LTIP_DURATION_OTHER && (
              <MpField label="Укажите длительность программы LTI" required htmlFor="mp-ltip-duration-other">
                <input
                  id="mp-ltip-duration-other"
                  className="dcf-input"
                  placeholder="Например: 6 лет, 18 месяцев"
                  value={form.ltipProgramDurationOther}
                  onChange={(e) => patch({ ltipProgramDurationOther: e.target.value })}
                />
              </MpField>
            )}

            <MpField label="Каким образом происходит переход прав или выплаты (вестинг) на ваш LTI?" required>
              <RadioValueList
                name="mp-ltip-vesting"
                value={form.ltipVestingType}
                options={LTIP_VESTING_OPTIONS}
                onChange={(vestingType) => patch({ ltipVestingType: vestingType })}
              />
            </MpField>

            <LtipValueInput
              label="Оцените выплату по программе LTI за последний период (год)"
              required
              mode={form.ltipLastPayoutMode}
              pct={form.ltipLastPayoutPct}
              amount={form.ltipLastPayoutAmount}
              pctId="mp-ltip-last-payout-pct"
              amountId="mp-ltip-last-payout-amount"
              onModeChange={(mode) => patch({
                ltipLastPayoutMode: mode,
                ltipLastPayoutPct: mode === 'percent' ? form.ltipLastPayoutPct : '',
                ltipLastPayoutAmount: mode === 'absolute' ? form.ltipLastPayoutAmount : '',
              })}
              onPctChange={(value) => patch({ ltipLastPayoutPct: value })}
              onAmountChange={(value) => patch({ ltipLastPayoutAmount: value })}
            />
          </>
        )}
      </div>
    </>
  );
}

export default function PositionQuestionnaire({ initial, onComplete, onBack }: Props) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<PositionFormData>(() => normalizePositionFormData(initial));
  const [error, setError] = useState<string | null>(null);

  const validateStep = (s: number): string | null => {
    if (s === 0) return validateBaseSalaryStep(form);
    if (s === 1) return validateStiStep(form);
    if (s === 2) return validateLtipStep(form);
    return null;
  };

  const goNext = () => {
    const err = validateStep(step);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const goPrev = () => {
    setError(null);
    if (step === 0) {
      onBack();
      return;
    }
    setStep((s) => s - 1);
  };

  const submit = () => {
    for (let s = 0; s < STEPS.length; s += 1) {
      const err = validateStep(s);
      if (err) {
        setError(err);
        setStep(s);
        return;
      }
    }
    onComplete(normalizePositionFormData(form));
  };

  const annualPreview = useMemo(() => {
    const synced = syncAnnualBaseFromMonthly(form);
    return synced.baseGrossSalary ? formatRub(synced.baseGrossSalary) : null;
  }, [form.baseGrossSalaryMonthly]);

  return (
    <div className="dcf-mp-form">
      <button type="button" className="dcf-btn dcf-btn--ghost dcf-mp-back" onClick={onBack}>
        ← К профилю рынка
      </button>

      <header className="dcf-header">
        <h1 className="dcf-header__title">Данные о должности и компенсации</h1>
        <p className="dcf-header__desc">
          Данные из анкеты не будут отображаться в вашем профиле. Они используются только
          для формирования анонимной агрегированной статистики и подбора релевантных
          рыночных ориентиров.
        </p>
      </header>

      <MpStepper steps={STEPS} current={step} />

      <div className="dcf-mp-card dcf-mp-form-card">
        {step === 0 && <BaseSalaryStep form={form} setForm={setForm} />}

        {step === 1 && <StiStep form={form} setForm={setForm} />}

        {step === 2 && <LtipStep form={form} setForm={setForm} />}

        {step === 0 && annualPreview && (
          <p className="dcf-mp-form-hint mt-3">
            Годовой эквивалент для аналитики: {annualPreview}
          </p>
        )}

        {error && <p className="dcf-mp-error">{error}</p>}

        <div className="dcf-mp-form-actions">
          <button type="button" className="dcf-btn dcf-btn--secondary" onClick={goPrev}>
            {step === 0 ? 'Отмена' : 'Назад'}
          </button>
          <div className="dcf-mp-form-actions__right">
            {step < STEPS.length - 1 ? (
              <button type="button" className="dcf-btn dcf-btn--primary" onClick={goNext}>
                Далее
              </button>
            ) : (
              <button type="button" className="dcf-btn dcf-btn--primary" onClick={submit}>
                Подтвердить данные
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
